//#region import
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { PasswordInput } from '_atoms';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import validator from 'validator';
import { useDispatch, useSelector } from 'react-redux';
import {
  login,
  verifyMfa,
  resetMfa,
  authorize,
  selectRequiresMfa,
  selectMfaFactorId,
  selectMfaPhoneHint,
  selectMfaStatus,
  selectMfaError,
} from '_redux/slices/main-screen/user';
import { sendMfaSmsChallenge, cancelMfa } from '_services/user-service';
import { getAuth } from 'firebase/auth';
import { Colors } from '_styles';
//#endregion

const LoginPage = (props) => {
  //#region local state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [logging, setLogging] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [smsSent, setSmsSent] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);

  //#endregion

  //#region redux
  // redux state variables
  const dispatch = useDispatch();
  const userStatus = useSelector(state => state.user.status);
  const error = useSelector(state => state.user.error);
  const requiresMfa = useSelector(selectRequiresMfa);
  const mfaFactorId = useSelector(selectMfaFactorId);
  const mfaPhoneHint = useSelector(selectMfaPhoneHint);
  const mfaStatus = useSelector(selectMfaStatus);
  const mfaError = useSelector(selectMfaError);
  //#endregion

  // keep MFA UI visible while verifying / right after success so we don't flash the login form
  const showMfaStep = requiresMfa || mfaStatus === 'loading' || mfaStatus === 'succeeded';

  const notify = (text) => toast(text);
  //#region functions
  //submit function
  const handleSubmit = (e) => {

    let isValidEmail = validator.isEmail(email);
    let isValidPassword = validator.isStrongPassword(password);

    let errors = {};
    if (!isValidEmail) {
      notify("Please enter a valid email")
      errors.email = "Valid email required"
    }
    if (!isValidPassword) {
      notify("Incorrect password, please try again")
      errors.password = "Incorrect password"
    }
    if (password.length == 0) {
      notify("Please enter a password")
      errors.password = "Valid password required"
    }

    setErrors(errors)

    if (isValidEmail && isValidPassword) {
      setLogging(true);

      console.log("Dispatching login for ", email);
      dispatch(login({ email: email, password: password }));
    }
  };

  // submit the 6-digit MFA code
  const handleMfaSubmit = async (e) => {
    e.preventDefault();

    if (!mfaCode || mfaCode.length !== 6) {
      notify("Please enter the 6-digit verification code");
      setErrors({ mfa: "Valid 6-digit code required" });
      return;
    }

    setErrors({});
    try {
      await dispatch(verifyMfa(mfaCode)).unwrap();
      const currentUser = getAuth().currentUser;
      if (currentUser) {
        // ensure RootRouter sees the signed-in user even if auth listener races
        dispatch(authorize(currentUser));
      }
    } catch (err) {
      // error surfaced via mfaError selector
    }
  };

  // go back to the email + password step
  const handleCancelMfa = () => {
    cancelMfa();
    dispatch(resetMfa());
    setMfaCode("");
    setSmsSent(false);
    setErrors({});
  };
  //#endregion

  //#region use Effect
  useEffect(() => {
    if (userStatus == 'failed' && logging == true) {
      setErrors({ login: error })
    }
  }, [userStatus]);

  // clear any stale MFA state (e.g. persisted from an interrupted login)
  useEffect(() => {
    cancelMfa();
    dispatch(resetMfa());
  }, []);

  // when the second factor is SMS, trigger the challenge text message
  useEffect(() => {
    const sendSms = async () => {
      setSendingSms(true);
      try {
        await sendMfaSmsChallenge('login-mfa-recaptcha');
        setSmsSent(true);
        notify(`Verification code sent to ${mfaPhoneHint || 'your phone'}`);
      } catch (e) {
        console.error('Error sending SMS challenge:', e);
        setErrors({ mfa: 'Could not send the SMS code. Please try logging in again.' });
      } finally {
        setSendingSms(false);
      }
    };

    if (requiresMfa && mfaFactorId === 'phone' && !smsSent && !sendingSms) {
      sendSms();
    }
  }, [requiresMfa, mfaFactorId]);

  useEffect(() => {
    if (mfaStatus == 'failed') {
      setErrors({ mfa: mfaError });
    }
  }, [mfaStatus, mfaError]);
  //#endregion

  return (
    <>
      <div>
        <Container fluid={true}>

          <Row>
            <Col>

              <Form className="mt-3" style={{ width: '50%' }}>
                <Container>

                  {!showMfaStep ? (
                    <>
                      {/* Title */}
                      <p className='mt-5' style={{ fontSize: 26, fontWeight: 'bold' }}>Login</p>

                      <div className="mt-3" style={{}}>
                        <p style={{ fontSize: 18, fontWeight: '600', opacity: 0.6 }}>Welcome back to PRM Assist Portal! Login to view assistance bookings, PRM Club rewards and analytics.</p>
                      </div>

                      {/* Email */}
                      <Form.Group className="mb-3 mt-5" controlId="formEmail">
                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Email</Form.Label>
                        <Form.Control onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Enter Email" onType={() => { console.log('typing') }} />
                        {errors.email && (<p className="text-danger">{errors.email}</p>)}

                      </Form.Group>

                      {/* Password */}
                      <Col className="mt-5">
                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Password</Form.Label>
                        <PasswordInput setPassword={setPassword} />
                        {errors.password && (<p className="text-danger">{errors.password}</p>)}
                      </Col>

                      {/* login Button */}
                      <Container className="mt-5">
                        <Button variant="primary" onClick={handleSubmit} loading={true} style={{ width: 175 }}>
                          {
                            userStatus != 'loading' ?
                              'Log in'
                              : (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  <span className="visually-hidden">Loading...</span>
                                </>
                              )
                          }
                        </Button>
                        {errors.login && (<p className="text-danger">{errors.login}</p>)}


                      </Container>
                    </>
                  ) : (
                    <>
                      {/* MFA Title */}
                      <p className='mt-5' style={{ fontSize: 26, fontWeight: 'bold' }}>Two-Factor Authentication</p>

                      <div className="mt-3" style={{}}>
                        <p style={{ fontSize: 18, fontWeight: '600', opacity: 0.6 }}>
                          {mfaStatus === 'succeeded'
                            ? 'Verification successful. Signing you in...'
                            : mfaFactorId === 'phone'
                              ? `Enter the 6-digit code sent by SMS to ${mfaPhoneHint || 'your phone'}.`
                              : 'Enter the 6-digit code from your authenticator app.'}
                        </p>
                      </div>

                      {mfaStatus !== 'succeeded' && (
                        <>
                          {/* MFA Code */}
                          <Form.Group className="mb-3 mt-5" controlId="formMfaCode">
                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Verification Code</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="000000"
                              value={mfaCode}
                              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                              maxLength={6}
                              style={{ fontSize: '1.2em', letterSpacing: '0.2em' }}
                              disabled={mfaStatus === 'loading'}
                            />
                            {errors.mfa && (<p className="text-danger">{errors.mfa}</p>)}
                          </Form.Group>

                          {/* verify Button */}
                          <Container className="mt-5">
                            <Button
                              variant="primary"
                              onClick={handleMfaSubmit}
                              disabled={mfaStatus === 'loading' || sendingSms}
                              style={{ width: 175 }}
                            >
                              {
                                mfaStatus !== 'loading' ?
                                  'Verify'
                                  : (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="visually-hidden">Loading...</span>
                                    </>
                                  )
                              }
                            </Button>

                            <Button
                              variant="link"
                              onClick={handleCancelMfa}
                              disabled={mfaStatus === 'loading'}
                              className="ms-3"
                            >
                              Back to login
                            </Button>
                          </Container>
                        </>
                      )}

                      {mfaStatus === 'succeeded' && (
                        <Container className="mt-5">
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        </Container>
                      )}
                    </>
                  )}

                  {/* invisible recaptcha anchor for SMS MFA challenges */}
                  <div id="login-mfa-recaptcha" />

                </Container>
              </Form>
            </Col>
          </Row>
        </Container>

        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          toastStyle={{ color: 'black', fontSize: 22 }}
        />

      </div>
    </>
  );
};

export default LoginPage;
